import { Button, FlexBox, ModalBody, ModalHead } from '@dixa/lib-shared-habla';
import CategoryTreeInput from 'lib-knowledge-management-common/components/categoryTreeInput/index';
import { CollectionSelect } from 'lib-knowledge-management-common/components/collectionSelect';
import { ParentRef } from 'lib-knowledge-management-common/models/types';
import { extractTitleFromTranslations } from 'lib-knowledge-management-common/utils/translations';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback, useEffect, useState } from 'react';

import { useArticleCacheFetch } from 'hooks/useArticleCacheFetch';
import useArticleMove from 'hooks/useArticleMove';

type ArticleMoveModalProps = {
  articleId: string;
  collectionId: string | undefined;
};

export function ArticleMoveModal({
  articleId,
  collectionId: incomingCollectionId,
}: ArticleMoveModalProps): JSX.Element {
  const { hideModal } = useModal();

  const { successNotify, errorNotify } = useNotification();
  const [move, { loading }] = useArticleMove();
  const article = useArticleCacheFetch({
    articleId,
  });

  const [collectionId, setCollectionId] = useState<string | undefined>(
    incomingCollectionId,
  );

  const [newParentCategory, setNewParentCategory] = useState<
    ParentRef | undefined
  >(article?.parent?.__typename === 'Category' ? article.parent : undefined);

  useEffect(
    () => setNewParentCategory(article?.parent || undefined),
    [article],
  );

  const articleTitle = article && extractTitleFromTranslations(article.titles);

  const moveArticle = useCallback(
    ({ newParent }: { newParent: ParentRef }) => {
      return move({
        articleId,
        newCategoryId: newParent.id,
        newCollectionId: collectionId,
      })
        .then(() => {
          hideModal();
          successNotify({
            title: 'Article Moved',
            text: `"${articleTitle}" has been moved`,
          });
        })
        .catch(() => {
          errorNotify({
            text: `"${articleTitle}" could not be moved`,
          });
        });
    },
    [
      move,
      articleId,
      collectionId,
      hideModal,
      successNotify,
      articleTitle,
      errorNotify,
    ],
  );

  if (!article) return <></>;

  const isDisabled =
    loading ||
    // Can't move if parent doesn't change
    newParentCategory?.id === article.parent?.id;

  return (
    <ModalWrapper>
      <ModalHead title="Move Article" onClose={hideModal}></ModalHead>
      <ModalBody>
        <CollectionSelect
          collectionId={collectionId}
          setCollectionId={setCollectionId}
        />
        <CategoryTreeInput
          withCollectionOption={false}
          value={newParentCategory}
          onChange={setNewParentCategory}
          // We don't want to prevent moving into child categories of current parent category.
          currentCategoryId={undefined}
          collectionId={collectionId}
        />

        <FlexBox justifyContent="flex-end" gap="100">
          <Button disabled={loading} variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
          <Button
            disabled={isDisabled}
            onClick={() => moveArticle({ newParent: newParentCategory! })}
          >
            Save
          </Button>
        </FlexBox>
      </ModalBody>
    </ModalWrapper>
  );
}
